import {
  Users,
  MapPin,
  Calendar,
  DollarSign,
  TrendingUp,
  Shield,
  Settings,
  BarChart3,
  UserCheck,
  AlertTriangle,
  Activity,
  Download,
  Server,
  Database,
  Zap,
} from "lucide-react";
import type React from "react";
import { useState, useEffect } from "react";

import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { useAuth } from "../../../hooks/useAuth";
import {
  useGetAdminAnalyticsQuery,
  useGetDashboardStatsQuery,
  useGetUserStatsQuery,
  useGetRecentActivityQuery,
  useExportAnalyticsReportQuery,
  useTrackEventMutation,
} from "../../../store/api/analyticsApi";
import { formatCurrency, formatDateTime, capitalizeFirst } from "../../../utils/helpers";

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState("overview");

  // Fetch analytics data
  const { data: dashboardData, isLoading: dashboardLoading } = useGetDashboardStatsQuery();
  const { data: adminAnalyticsData, isLoading: adminLoading } = useGetAdminAnalyticsQuery();
  const { data: userStatsData, isLoading: userStatsLoading } = useGetUserStatsQuery();
  const { data: recentActivityData } = useGetRecentActivityQuery({ limit: 10 });
  const { data: reportData, refetch: exportReport } = useExportAnalyticsReportQuery(
    { period: "monthly" },
    { skip: true }
  );

  // Track events mutation
  const [trackEvent] = useTrackEventMutation();

  const dashboardStats = dashboardData?.data;
  const adminAnalytics = adminAnalyticsData?.data;
  const userStats = userStatsData?.data;
  const recentActivities = recentActivityData?.data || [];

  // Track dashboard view
  useEffect(() => {
    if (user?.id) {
      trackEvent({
        type: "system_health",
        metadata: { userRole: "superadmin", action: "dashboard_viewed", timestamp: new Date() },
      });
    }
  }, [user?.id, trackEvent]);

  const handleExportReport = async () => {
    try {
      await exportReport();
      // Handle download logic here - you might want to show a success message
      // or trigger actual file download based on your backend implementation
    } catch (error) {
      console.error("Failed to export report:", error);
    }
  };

  if (dashboardLoading || adminLoading) {
    return (
      <div className='container py-8'>
        <div className='animate-pulse'>
          <div className='mb-4 h-8 w-1/3 rounded bg-gray-200'></div>
          <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-5'>
            {[...Array(5)].map((_, i) => (
              <div key={i} className='h-24 rounded bg-gray-200'></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='container py-8'>
      <div className='mb-8 flex items-center justify-between'>
        <div>
          <h1 className='mb-2 text-3xl font-bold'>Admin Dashboard</h1>
          <p className='text-muted-foreground'>System overview and management tools.</p>
        </div>
        <div className='flex gap-2'>
          <Button variant='outline' onClick={handleExportReport}>
            <Download className='mr-2 h-4 w-4' />
            Export Report
          </Button>
          <Button>
            <Settings className='mr-2 h-4 w-4' />
            System Settings
          </Button>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-5'>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Users</CardTitle>
            <Users className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {dashboardStats?.users?.total?.toLocaleString() || 0}
            </div>
            <p className='text-xs text-muted-foreground'>
              +{dashboardStats?.users?.newThisMonth || 0} this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Grounds</CardTitle>
            <MapPin className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{dashboardStats?.grounds?.total || 0}</div>
            <p className='text-xs text-muted-foreground'>
              {dashboardStats?.grounds?.active || 0} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Events</CardTitle>
            <Calendar className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{dashboardStats?.events?.total || 0}</div>
            <p className='text-xs text-muted-foreground'>
              {dashboardStats?.events?.upcoming || 0} upcoming
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Revenue</CardTitle>
            <DollarSign className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {formatCurrency(dashboardStats?.revenue?.totalRevenue || 0)}
            </div>
            <p className='text-xs text-muted-foreground'>
              +{dashboardStats?.revenue?.revenueGrowth?.toFixed(1) || 0}% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>System Health</CardTitle>
            <Server className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold text-green-600'>
              {adminAnalytics?.systemHealth?.uptime?.toFixed(1) || 99.9}%
            </div>
            <p className='text-xs text-muted-foreground'>Uptime</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab} className='space-y-6'>
        <TabsList>
          <TabsTrigger value='overview'>Overview</TabsTrigger>
          <TabsTrigger value='users'>Users</TabsTrigger>
          <TabsTrigger value='grounds'>Grounds</TabsTrigger>
          <TabsTrigger value='analytics'>Analytics</TabsTrigger>
          <TabsTrigger value='alerts'>Alerts</TabsTrigger>
          <TabsTrigger value='system'>System</TabsTrigger>
        </TabsList>

        <TabsContent value='overview' className='space-y-6'>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Activity className='h-5 w-5' />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-4'>
                  {recentActivities.length > 0 ? (
                    recentActivities.slice(0, 5).map((activity: any, index: number) => (
                      <div key={index} className='flex items-center justify-between'>
                        <div>
                          <h4 className='font-medium'>
                            {capitalizeFirst(activity.type.replace(/_/g, " "))}
                          </h4>
                          <p className='text-sm text-muted-foreground'>
                            {formatDateTime(activity.timestamp)}
                          </p>
                        </div>
                        <Badge
                          variant={activity.type.includes("error") ? "destructive" : "default"}
                        >
                          <Activity className='mr-1 h-3 w-3' />
                          {activity.type.includes("error") ? "Error" : "Success"}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <div className='py-4 text-center text-muted-foreground'>
                      No recent activities
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <TrendingUp className='h-5 w-5' />
                  Platform Growth
                </CardTitle>
              </CardHeader>
              <CardContent>
                {adminAnalytics?.charts?.userGrowth && (
                  <div className='space-y-3'>
                    {adminAnalytics.charts.userGrowth.slice(-6).map((month: any, index: number) => (
                      <div key={index} className='flex items-center justify-between'>
                        <span className='font-medium'>{month.month}</span>
                        <div className='flex items-center gap-4'>
                          <span className='text-sm text-muted-foreground'>
                            +{month.count} users
                          </span>
                          <span className='text-sm font-medium text-green-600'>
                            {month.growth > 0 ? "+" : ""}
                            {month.growth?.toFixed(1) || 0}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* System Performance Overview */}
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Zap className='h-5 w-5' />
                  System Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                {adminAnalytics?.systemHealth && (
                  <div className='grid grid-cols-2 gap-4'>
                    <div className='text-center'>
                      <div className='text-xl font-bold text-green-600'>
                        {adminAnalytics.systemHealth.uptime?.toFixed(2) || 99.9}%
                      </div>
                      <p className='text-xs text-muted-foreground'>Uptime</p>
                    </div>
                    <div className='text-center'>
                      <div className='text-xl font-bold'>
                        {adminAnalytics.systemHealth.averageResponseTime || 0}ms
                      </div>
                      <p className='text-xs text-muted-foreground'>Response Time</p>
                    </div>
                    <div className='text-center'>
                      <div className='text-xl font-bold text-red-600'>
                        {adminAnalytics.systemHealth.errorRate?.toFixed(2) || 0}%
                      </div>
                      <p className='text-xs text-muted-foreground'>Error Rate</p>
                    </div>
                    <div className='text-center'>
                      <div className='text-xl font-bold text-blue-600'>
                        {adminAnalytics.systemHealth.activeUsers || 0}
                      </div>
                      <p className='text-xs text-muted-foreground'>Active Now</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <BarChart3 className='h-5 w-5' />
                  Quick Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  <div className='flex justify-between'>
                    <span>Total Bookings:</span>
                    <span className='font-medium'>{dashboardStats?.bookings?.total || 0}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Monthly Revenue:</span>
                    <span className='font-medium text-green-600'>
                      {formatCurrency(dashboardStats?.revenue?.monthlyRevenue || 0)}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Active Users:</span>
                    <span className='font-medium text-blue-600'>
                      {dashboardStats?.users?.active || 0}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Verified Users:</span>
                    <span className='font-medium'>{dashboardStats?.users?.verifiedUsers || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value='users' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>User Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              {userStats ? (
                <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
                  <div>
                    <h3 className='mb-4 font-semibold'>User Status</h3>
                    <div className='space-y-2'>
                      <div className='flex justify-between'>
                        <span>Active Users:</span>
                        <span className='font-medium text-green-600'>
                          {userStats.active.toLocaleString()}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span>Inactive Users:</span>
                        <span className='font-medium text-gray-600'>
                          {userStats.inactive.toLocaleString()}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span>Suspended Users:</span>
                        <span className='font-medium text-red-600'>
                          {userStats.suspended.toLocaleString()}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span>Verified Users:</span>
                        <span className='font-medium text-blue-600'>
                          {userStats.verified.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className='mb-4 font-semibold'>User Roles</h3>
                    <div className='space-y-2'>
                      {Object.entries(userStats.byRole).map(([role, count]) => (
                        <div key={role} className='flex justify-between'>
                          <span>{capitalizeFirst(role)}:</span>
                          <span className='font-medium'>{count.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className='mb-4 font-semibold'>Growth Stats</h3>
                    <div className='space-y-2'>
                      <div className='flex justify-between'>
                        <span>Daily:</span>
                        <span className='font-medium text-green-600'>
                          +{userStats.growthStats.daily}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span>Weekly:</span>
                        <span className='font-medium text-green-600'>
                          +{userStats.growthStats.weekly}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span>Monthly:</span>
                        <span className='font-medium text-green-600'>
                          +{userStats.growthStats.monthly}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='py-8 text-center'>
                  <Users className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                  <p className='text-muted-foreground'>Loading user statistics...</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* User Engagement */}
          {userStats?.engagementStats && (
            <Card>
              <CardHeader>
                <CardTitle>User Engagement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-1 gap-6 md:grid-cols-4'>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-blue-600'>
                      {userStats.engagementStats.dailyActiveUsers.toLocaleString()}
                    </div>
                    <p className='text-sm text-muted-foreground'>Daily Active</p>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-green-600'>
                      {userStats.engagementStats.weeklyActiveUsers.toLocaleString()}
                    </div>
                    <p className='text-sm text-muted-foreground'>Weekly Active</p>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-purple-600'>
                      {userStats.engagementStats.monthlyActiveUsers.toLocaleString()}
                    </div>
                    <p className='text-sm text-muted-foreground'>Monthly Active</p>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold'>
                      {Math.round(userStats.engagementStats.averageSessionDuration)} min
                    </div>
                    <p className='text-sm text-muted-foreground'>Avg Session</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value='grounds' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>Ground Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <div>
                  <h3 className='mb-4 font-semibold'>Ground Status</h3>
                  <div className='space-y-2'>
                    <div className='flex justify-between'>
                      <span>Active:</span>
                      <Badge variant='default'>{dashboardStats?.grounds?.active || 0}</Badge>
                    </div>
                    <div className='flex justify-between'>
                      <span>Inactive:</span>
                      <Badge variant='secondary'>{dashboardStats?.grounds?.inactive || 0}</Badge>
                    </div>
                    <div className='flex justify-between'>
                      <span>Maintenance:</span>
                      <Badge variant='destructive'>
                        {dashboardStats?.grounds?.maintenance || 0}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className='mb-4 font-semibold'>Quick Actions</h3>
                  <div className='space-y-2'>
                    <Button variant='outline' className='w-full justify-start'>
                      <MapPin className='mr-2 h-4 w-4' />
                      View All Grounds
                    </Button>
                    <Button variant='outline' className='w-full justify-start'>
                      <Settings className='mr-2 h-4 w-4' />
                      Ground Settings
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='analytics' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>System Analytics</CardTitle>
            </CardHeader>
            <CardContent>
              {adminAnalytics?.charts && (
                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                  <div>
                    <h3 className='mb-4 font-semibold'>Revenue Growth</h3>
                    <div className='space-y-2'>
                      {adminAnalytics.charts.revenueGrowth.slice(-6).map((item, index) => (
                        <div key={index} className='flex justify-between'>
                          <span>{item.month}:</span>
                          <span className='font-medium'>{formatCurrency(item.revenue)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className='mb-4 font-semibold'>Top Events</h3>
                    <div className='space-y-2'>
                      {adminAnalytics.charts.eventPerformance.slice(0, 5).map((event, index) => (
                        <div key={index} className='flex justify-between'>
                          <span className='mr-2 truncate'>{event.eventTitle}:</span>
                          <span className='font-medium'>{formatCurrency(event.totalRevenue)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {adminAnalytics?.insights && (
                <div className='mt-6 rounded-lg bg-blue-50 p-4'>
                  <h3 className='mb-2 font-semibold'>Insights & Recommendations</h3>
                  <ul className='space-y-1'>
                    {adminAnalytics.insights.recommendations.map((recommendation, index) => (
                      <li key={index} className='text-sm text-blue-700'>
                        • {recommendation}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='alerts' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>System Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-4'>
                {adminAnalytics?.systemHealth?.errorRate &&
                  adminAnalytics?.systemHealth?.errorRate > 1 && (
                    <div className='flex items-center gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3'>
                      <AlertTriangle className='h-5 w-5 text-yellow-600' />
                      <div>
                        <h4 className='font-medium text-yellow-800'>High error rate</h4>
                        <p className='text-sm text-yellow-600'>
                          Error rate at {adminAnalytics.systemHealth.errorRate}%
                        </p>
                      </div>
                    </div>
                  )}

                {adminAnalytics?.systemHealth?.uptime &&
                  adminAnalytics?.systemHealth?.uptime > 99.5 && (
                    <div className='flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3'>
                      <UserCheck className='h-5 w-5 text-green-600' />
                      <div>
                        <h4 className='font-medium text-green-800'>System operational</h4>
                        <p className='text-sm text-green-600'>
                          {adminAnalytics.systemHealth.activeUsers} active users
                        </p>
                      </div>
                    </div>
                  )}

                {/* Low uptime alert */}
                {adminAnalytics?.systemHealth?.uptime &&
                  adminAnalytics?.systemHealth?.uptime < 99.0 && (
                    <div className='flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3'>
                      <AlertTriangle className='h-5 w-5 text-red-600' />
                      <div>
                        <h4 className='font-medium text-red-800'>System issues detected</h4>
                        <p className='text-sm text-red-600'>
                          Uptime below normal at {adminAnalytics.systemHealth.uptime.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  )}
              </div>
            </CardContent>
          </Card>

          {adminAnalytics?.platformMetrics && (
            <Card>
              <CardHeader>
                <CardTitle>Platform Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-4'>
                  <div className='text-center'>
                    <div className='text-2xl font-bold'>
                      {adminAnalytics.platformMetrics.totalUsers.toLocaleString()}
                    </div>
                    <p className='text-sm text-muted-foreground'>Total Users</p>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold'>
                      {formatCurrency(adminAnalytics.platformMetrics.totalRevenue)}
                    </div>
                    <p className='text-sm text-muted-foreground'>Total Revenue</p>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold'>
                      {adminAnalytics.platformMetrics.transactionVolume.toLocaleString()}
                    </div>
                    <p className='text-sm text-muted-foreground'>Transactions</p>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold'>
                      {adminAnalytics.platformMetrics.customerSatisfaction}/5
                    </div>
                    <p className='text-sm text-muted-foreground'>Satisfaction</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value='system' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>System Settings</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <Button className='h-auto justify-start bg-transparent p-4' variant='outline'>
                  <div className='flex items-center gap-3'>
                    <Shield className='h-5 w-5' />
                    <div className='text-left'>
                      <div className='font-medium'>Security Settings</div>
                      <div className='text-sm text-muted-foreground'>Manage security policies</div>
                    </div>
                  </div>
                </Button>
                <Button className='h-auto justify-start bg-transparent p-4' variant='outline'>
                  <div className='flex items-center gap-3'>
                    <Settings className='h-5 w-5' />
                    <div className='text-left'>
                      <div className='font-medium'>System Configuration</div>
                      <div className='text-sm text-muted-foreground'>Configure system settings</div>
                    </div>
                  </div>
                </Button>
                <Button className='h-auto justify-start bg-transparent p-4' variant='outline'>
                  <div className='flex items-center gap-3'>
                    <BarChart3 className='h-5 w-5' />
                    <div className='text-left'>
                      <div className='font-medium'>Analytics Configuration</div>
                      <div className='text-sm text-muted-foreground'>
                        Configure analytics settings
                      </div>
                    </div>
                  </div>
                </Button>
                <Button className='h-auto justify-start bg-transparent p-4' variant='outline'>
                  <div className='flex items-center gap-3'>
                    <Database className='h-5 w-5' />
                    <div className='text-left'>
                      <div className='font-medium'>Database Management</div>
                      <div className='text-sm text-muted-foreground'>
                        Manage database operations
                      </div>
                    </div>
                  </div>
                </Button>
              </div>

              {adminAnalytics?.systemHealth && (
                <div className='mt-6'>
                  <h3 className='mb-4 font-semibold'>System Health Details</h3>
                  <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <div className='space-y-2'>
                      <div className='flex justify-between'>
                        <span>Uptime:</span>
                        <span className='font-medium text-green-600'>
                          {adminAnalytics.systemHealth.uptime}%
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span>Error Rate:</span>
                        <span className='font-medium text-yellow-600'>
                          {adminAnalytics.systemHealth.errorRate}%
                        </span>
                      </div>
                    </div>
                    <div className='space-y-2'>
                      <div className='flex justify-between'>
                        <span>Avg Response Time:</span>
                        <span className='font-medium'>
                          {adminAnalytics.systemHealth.averageResponseTime}ms
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span>Active Users:</span>
                        <span className='font-medium'>
                          {adminAnalytics.systemHealth.activeUsers}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
